// ============================================================
// caseName.ts — THE single source of a case's display name.
//
// Every surface that names a case (list card, quick-view modal, case page
// header, client chips, linked cases, sidebar recents/favourites, dashboard,
// My Work, …) calls caseName(). The backend has a byte-for-byte twin in
// backend/src/lib/caseName.ts for server-built labels (calendar, bell,
// email, day summary) — keep the two in step.
//
// Rule:
//   1. A user-set Case.caseName (explicit rename) wins when present.
//   2. Defendant present → "<claimant surname> v <defendant>":
//        • individual defendant → surname only        ("Fiddler v Bell")
//        • company / organisation → full name          ("Fiddler v Aviva Insurance Ltd")
//        • can't tell (e.g. one word) → full name
//   3. Otherwise → the client's full name ("Travis Fiddler"), else "New Case".
//
// The schema has no defendant-type column, so individual-vs-company is
// inferred from the name (company markers); anything ambiguous keeps the
// full name, per the agreed fallback.
// ============================================================

export interface CaseNameSource {
  caseName?: string | null
  clientForename?: string | null
  clientSurname?: string | null
  client?: { forename?: string | null; surname?: string | null } | null
  defendantName?: string | null
}

export const NEW_CASE_NAME = 'New Case'

// Words that mark an organisation rather than a person.
const COMPANY_MARKERS = /\b(ltd|limited|plc|llp|llc|lp|inc|incorporated|corp|corporation|co|company|companies|group|holdings?|insurance|insurers?|assurance|underwriting|mutual|council|borough|county|authority|trust|nhs|services|solutions|partners|partnership|associates|international|bank|agency|department|ministry|police|constabulary|hospital|university|school|college|academy|club|society|association|foundation|church|transport|logistics|motors?|haulage|hire|leasing|rentals?|garage|construction|contractors?|properties|property|estates?|management|enterprises?|industries|systems|technologies|uk|gb|t\/a)\b|&|\+/i

// Honorifics stripped before taking an individual's surname ("Mr Ollie Bell" → "Bell").
const HONORIFICS = /^(mr|mrs|ms|miss|mx|dr|prof|sir|dame|lord|lady|rev)\.?$/i
// Name suffixes skipped when finding the surname ("John Smith Jr" → "Smith").
const SUFFIXES = /^(jr|jnr|sr|snr|ii|iii|iv)\.?$/i

// A single name word: letters (incl. Latin accents), apostrophes, hyphens.
// (Explicit ranges, not \p{L} — the frontend targets ES5, no `u` flag.)
const NAME_WORD = /^[A-Za-z\u00C0-\u00D6\u00D8-\u00F6\u00F8-\u017F][A-Za-z\u00C0-\u00D6\u00D8-\u00F6\u00F8-\u017F'\u2019-]*$/

const clean = (s: string | null | undefined): string => (s ?? '').replace(/\s+/g, ' ').trim()

// True when the defendant name reads as a company / organisation.
export const isCompanyName = (name: string): boolean => COMPANY_MARKERS.test(name)

// The defendant as it appears after "v": surname for a clear individual,
// full name for a company or anything we can't classify.
export const defendantShortName = (defendant: string): string => {
  const full = clean(defendant)
  if (!full || isCompanyName(full)) return full
  const words = full.split(' ').filter((w) => !HONORIFICS.test(w))
  while (words.length > 1 && SUFFIXES.test(words[words.length - 1])) words.pop()
  // Only a 2–4 word, letters-only name is confidently a person.
  const personLike = words.length >= 2 && words.length <= 4 && words.every((w) => NAME_WORD.test(w))
  return personLike ? words[words.length - 1] : full
}

// The claimant's (client's) full name — the snapshot first, then the live Client.
export const caseClientName = (c: CaseNameSource): string =>
  clean(`${clean(c.clientForename) || clean(c.client?.forename)} ${clean(c.clientSurname) || clean(c.client?.surname)}`)

// THE case display name.
export const caseName = (c: CaseNameSource): string => {
  const own = clean(c.caseName)
  if (own) return own
  const surname = clean(c.clientSurname) || clean(c.client?.surname)
  const defendant = clean(c.defendantName)
  if (surname && defendant) return `${surname} v ${defendantShortName(defendant)}`
  return caseClientName(c) || NEW_CASE_NAME
}

// ── THE CASE REFERENCE ──────────────────────────────────────
// The ONE canonical, human reference for a case: SURNAME-YEAR-ID, e.g.
// "FIDDLER-2026-A1B2C3". (Was already the "Copy reference" / case-header form —
// now used everywhere a reference is shown.)
//   • SURNAME — the claimant (client) surname, snapshot first then the live
//     Client (same precedence as caseName); upper-cased. None → "DRAFT".
//   • YEAR    — the case's createdAt year (UTC, so it's identical on every
//     machine + the server — stable and sortable).
//   • ID      — the first 6 chars of the case id, upper-cased. The raw id stays
//     recoverable as a prefix match (code that needs the id keeps using c.id).
export interface CaseRefSource {
  id: string
  createdAt: string | Date
  clientSurname?: string | null
  client?: { surname?: string | null } | null
}
export const caseShortId = (id: string): string => id.slice(0, 6).toUpperCase()
export const caseRef = (c: CaseRefSource): string => {
  const surname = clean(c.clientSurname) || clean(c.client?.surname)
  const created = new Date(c.createdAt)
  const year = Number.isNaN(created.getTime()) ? '0000' : String(created.getUTCFullYear())
  return `${surname ? surname.toUpperCase() : 'DRAFT'}-${year}-${caseShortId(c.id)}`
}

// "<name> · <REF>" — the label used for favourites / calendar-style chips.
export const caseNameWithRef = (c: CaseNameSource & CaseRefSource): string => `${caseName(c)} · ${caseRef(c)}`
